import { makeStyles, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, tokens } from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import type { PlanwertKostenErloeseItem } from '../../models/PlanwertKostenErloeseItem';
import type { PlanwertSollstundenProjektmitarbeiterItem } from '../../models/PlanwertSollstundenProjektmitarbeiterItem';
import type { ProjektItem } from '../../models/ProjektItem';
import type { DataService } from '../../services/dataService';

const formatCurrency = (value: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);

const formatHours = (value: number) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const useStyles = makeStyles({
    tileRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalM,
        marginBottom: tokens.spacingVerticalM,
    },
    tile: {
        flex: '1 1 180px',
        minWidth: '180px',
        display: 'flex',
        flexDirection: 'column',
    },
    tileHeader: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
        fontWeight: 700,
    },
    tileValue: {
        backgroundColor: tokens.colorNeutralBackground3,
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
        color: tokens.colorBrandForeground1,
    },
    tableWrapper: {
        width: 'fit-content',
        minWidth: '50%',
        maxWidth: '100%',
        overflowX: 'auto',
        marginBottom: tokens.spacingVerticalM,
    },
});

const headerCellStyle = {
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    fontWeight: 700,
};

type TileProps = { label: string; value: string };

const Tile: React.FC<TileProps> = ({ label, value }) => {
    const styles = useStyles();
    return (
        <div className={styles.tile}>
            <div className={styles.tileHeader}>{label}</div>
            <div className={styles.tileValue}>{value}</div>
        </div>
    );
};

type EckdatenOverviewFinancialDataProps = {
    project: ProjektItem;
    dataService: DataService | null;
};

export const EckdatenOverviewFinancialData: React.FC<EckdatenOverviewFinancialDataProps> = ({ project, dataService }) => {
    const styles = useStyles();
    const [aliquotByPersonal, setAliquotByPersonal] = useState<Map<number, number>>(new Map());
    const [orderSums, setOrderSums] = useState<{ neu: number; offen: number; abgeschlossen: number }>({ neu: 0, offen: 0, abgeschlossen: 0 });

    useEffect(() => {
        if (!dataService || !project.projektID) return;
        let cancelled = false;
        dataService
            .getProjektkostenListeAlleBestellungen(project.projektID)
            .then((items: PlanwertKostenErloeseItem[]) => {
                if (cancelled) return;
                const sumOf = (status: string) =>
                    items
                        .filter((item) => (item.positionBezeichnung ?? '').trim().toLowerCase() === status)
                        .reduce((sum, item) => sum + (item.betrag ?? 0), 0);
                setOrderSums({ neu: sumOf('neu'), offen: sumOf('offen'), abgeschlossen: sumOf('abgeschlossen') });
            })
            .catch((err: unknown) => console.error('Failed to load orders', err));
        return () => {
            cancelled = true;
        };
    }, [dataService, project.projektID]);

    useEffect(() => {
        if (!dataService || !project.projektID) return;
        let cancelled = false;
        dataService
            .getPlanwerteSollstundenProjektmitarbeiterListe(project.projektID)
            .then((items: PlanwertSollstundenProjektmitarbeiterItem[]) => {
                if (cancelled) return;
                const sums = new Map<number, number>();
                items.forEach((item) => sums.set(item.personalID, (sums.get(item.personalID) ?? 0) + (item.sollstunden ?? 0)));
                setAliquotByPersonal(sums);
            })
            .catch((err: unknown) => console.error('Failed to load planned hours', err));
        return () => {
            cancelled = true;
        };
    }, [dataService, project.projektID]);

    const erhalten = project.zahlungseingaengeBetragBrutto ?? 0;
    const realeKostenExklZA =
        (project.externeKostenIst ?? 0) + (project.sonstigeInterneKostenIst ?? 0) + (project.personalkostenLohnverrechnungIst ?? 0);
    const realeZAKosten = project.personalkostenZeitaufzeichnungIst ?? 0;
    const db1 = erhalten - realeKostenExklZA;
    const db2 = db1 - realeZAKosten;

    const mitarbeiter = project.projektmitarbeiter ?? [];

    return (
        <div>
            <div className={styles.tileRow}>
                <Tile label="Funding amount per contract" value={formatCurrency(project.foerdersumme ?? 0)} />
                <Tile label="Invoiced" value={formatCurrency(project.rechnungenBetragBrutto ?? 0)} />
                <Tile label="Received" value={formatCurrency(erhalten)} />
            </div>

            <div className={styles.tileRow}>
                <Tile label="Full costs incl. overhead excl. SR" value={formatCurrency(project.kostenPlan ?? 0)} />
                <Tile label="Actual costs excl. ZA" value={formatCurrency(realeKostenExklZA)} />
                <Tile label="Actual ZA costs (time recording)" value={formatCurrency(realeZAKosten)} />
                <Tile label="Contribution margin 1 (Received - actual costs excl. ZA)" value={formatCurrency(db1)} />
                <Tile label="Contribution margin 2 (CM 1 - actual ZA costs)" value={formatCurrency(db2)} />
            </div>

            <div className={styles.tileRow}>
                <Tile label="Order total new" value={formatCurrency(orderSums.neu)} />
                <Tile label="Order total open" value={formatCurrency(orderSums.offen)} />
                <Tile label="Order total completed" value={formatCurrency(orderSums.abgeschlossen)} />
            </div>

            <div className={styles.tableWrapper}>
                <Table size="small" aria-label="Employee hours">
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell style={headerCellStyle}>Employee</TableHeaderCell>
                            <TableHeaderCell style={headerCellStyle}>Target hours total</TableHeaderCell>
                            <TableHeaderCell style={headerCellStyle}>Target hours prorated</TableHeaderCell>
                            <TableHeaderCell style={headerCellStyle}>Actual hours</TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {mitarbeiter.map((m) => (
                            <TableRow key={m.itemId}>
                                <TableCell>{m.fullName}</TableCell>
                                <TableCell>{formatHours(m.sollstundenGesamt ?? 0)}</TableCell>
                                <TableCell>{formatHours(aliquotByPersonal.get(m.personalID) ?? 0)}</TableCell>
                                <TableCell>{formatHours(m.iststunden ?? 0)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <div className={styles.tileRow}>
                <Tile label="Project start" value={project.operativerBeginnIstAsText || '-'} />
                <Tile label="Project end" value={project.operativesEndeIstAsText || '-'} />
                <Tile label="Next report due" value={project.nextBericht || '-'} />
                <Tile label="Report Type" value={'-'} />
            </div>

            <div className={styles.tileRow}>
                <Tile label="Funding source" value={project.foerdergeberNeu || '-'} />
                <Tile label="Number of publications" value={String(project.anzahlPublikationen ?? 0)} />
            </div>
        </div>
    );
};
